import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, X, Package, Save } from "lucide-react";

// Feb 2026 · Employee self-service — "My Assets".
// Shows only assets currently allocated to the caller's `employee_id`. Each
// row exposes an Edit Credentials modal that upserts against the vault via
// /api/hr/v2/my-credentials (employee-only endpoint; cannot touch other
// people's records).

const CRED_FIELDS = [
  { key: "gmail",  label: "Gmail" },
  { key: "zoho",   label: "Zoho" },
  { key: "laptop", label: "Laptop" },
  { key: "mobile", label: "Mobile" },
  { key: "apple",  label: "Apple / iCloud / Mac" },
];

export default function HrMyAssetsPage() {
  const [data, setData] = useState({ employee_id: null, assets: [], credentials: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/v2/my-assets");
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const credByAsset = useMemo(() => {
    const m = {};
    (data.credentials || []).forEach((c) => { m[c.asset_id] = c; });
    return m;
  }, [data.credentials]);

  if (loading) return <div className="p-10 text-xs font-mono text-gray-500">Loading your assets…</div>;

  if (!data.employee_id) {
    return (
      <div className="p-10">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">HR · My Assets</p>
        <h1 className="font-display text-3xl text-white mt-1">No Employee ID linked</h1>
        <p className="text-sm text-gray-400 mt-3">Your account doesn't have an Employee ID yet. Reach out to HR to get one issued.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">HR · My Assets</p>
        <h1 className="font-display text-3xl text-white mt-1">Your assigned inventory</h1>
        <p className="text-xs text-gray-500 mt-1">Employee ID <span className="text-gray-300 font-mono">{data.employee_id}</span> · {data.assets.length} asset(s) assigned</p>
      </div>

      <div className="px-10 py-8">
        {data.assets.length === 0 ? (
          <div className="iz-card p-10 text-center">
            <Package className="w-8 h-8 text-gray-700 mx-auto mb-3"/>
            <p className="text-sm text-gray-400">No assets are currently allocated to you.</p>
            <p className="text-[10px] font-mono text-gray-600 mt-2">Once HR allocates one, it will appear here with an "Edit Credentials" action.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.assets.map((a) => {
              const c = credByAsset[a.asset_id];
              return (
                <div key={a.asset_id} className="iz-card p-5" data-testid={`my-asset-${a.asset_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{a.category}</p>
                      <p className="text-white font-mono mt-1">{a.asset_id}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{a.brand} {a.model}</p>
                      <p className="text-[10px] font-mono text-gray-500 mt-2">SN {a.serial_no}</p>
                    </div>
                    <button onClick={() => setEditing({ asset: a, cred: c })}
                            data-testid={`edit-creds-${a.asset_id}`}
                            className="iz-btn-secondary text-xs flex items-center gap-1.5">
                      <KeyRound className="w-3.5 h-3.5"/> Edit Credentials
                    </button>
                  </div>
                  {c ? (
                    <p className="text-[10px] font-mono text-gray-500">
                      Vault entry exists · last updated {new Date(c.updated_at).toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-[10px] font-mono text-gray-600 italic">No credentials saved yet · click Edit Credentials to add.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <SelfCredentialModal
          asset={editing.asset}
          existing={editing.cred}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SelfCredentialModal({ asset, existing, onClose, onSaved }) {
  const [reveal, setReveal] = useState(false);
  const [form, setForm] = useState(() => {
    const seed = { asset_id: asset.asset_id };
    CRED_FIELDS.forEach(({ key }) => {
      seed[`${key}_username`] = existing?.[`${key}_username`] || "";
      seed[`${key}_password`] = existing?.[`${key}_password`] || "";
    });
    return seed;
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/hr/v2/my-credentials", payload);
      toast.success("Credentials saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl iz-card p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Credentials</p>
        <h2 className="font-display text-2xl text-white mb-1">{asset.asset_id}</h2>
        <p className="text-xs text-gray-500 mb-6">{asset.category} · {asset.brand} · SN {asset.serial_no}</p>

        <div className="flex items-center justify-end mb-4">
          <button type="button" onClick={() => setReveal((v) => !v)}
                  className="text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#d4af37] flex items-center gap-1"
                  data-testid="my-cred-reveal">
            {reveal ? <><EyeOff className="w-3 h-3"/> Hide</> : <><Eye className="w-3 h-3"/> Reveal</>}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {CRED_FIELDS.map(({ key, label }) => (
            <div key={key} className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label} Username</label>
                <input value={form[`${key}_username`]}
                       onChange={(e) => setForm(f => ({...f, [`${key}_username`]: e.target.value}))}
                       className="iz-input" data-testid={`my-cred-${key}-user`}/>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label} Password</label>
                <input type={reveal ? "text" : "password"} value={form[`${key}_password`]}
                       onChange={(e) => setForm(f => ({...f, [`${key}_password`]: e.target.value}))}
                       className="iz-input" data-testid={`my-cred-${key}-pass`}/>
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-1.5" data-testid="my-cred-save">
              <Save className="w-3.5 h-3.5"/> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>

        <p className="text-[10px] font-mono text-gray-600 mt-4 text-center italic">
          Only usernames + passwords are editable. Asset ID and Employee ID are locked.
        </p>
      </div>
    </div>
  );
}
